import { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { Briefcase, Loader2, AlertTriangle, X, FileText, History, Save, Wand2, Eye, ShieldCheck, Upload, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";

export default function AdminMercato() {
    const [tab, setTab] = useState("listings");
    const [listings, setListings] = useState([]);
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState("active");
    const [target, setTarget] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        const [l, s] = await Promise.all([
            apiPrivate.get(`/admin/market/listings?only_active=${filter === "active"}`),
            apiPrivate.get("/admin/market/stats"),
        ]);
        setListings(l.data || []);
        setStats(s.data);
        setLoading(false);
    }, [filter]);
    useEffect(() => { if (tab === "listings") load(); }, [tab, load]);

    return (
        <AppLayout admin>
            <div className="max-w-[1400px] mx-auto" data-testid="admin-mercato">
                <span className="label-tag text-magenta">Bacheca annunci soci</span>
                <h1 className="font-display text-5xl mt-3 tracking-tighter">
                    Moderazione <span className="font-serif-italic text-gradient-tropical">annunci</span>.
                </h1>

                {/* Tabs */}
                <div className="mt-8 flex items-center gap-2 border-b border-white/10">
                    <TabBtn active={tab === "listings"} onClick={() => setTab("listings")} testid="m-tab-listings" icon={Briefcase}>Annunci</TabBtn>
                    <TabBtn active={tab === "preemption"} onClick={() => setTab("preemption")} testid="m-tab-preemption" icon={ShieldCheck}>Prelazione Quote A</TabBtn>
                    <TabBtn active={tab === "disclaimer"} onClick={() => setTab("disclaimer")} testid="m-tab-disclaimer" icon={FileText}>Disclaimer</TabBtn>
                </div>

                {tab === "preemption" && <PreemptionPanel />}

                {tab === "listings" ? (
                    <>
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
                        <StatCard label="Inserzioni totali" value={stats.total_listings} />
                        <StatCard label="Attive" value={stats.active_listings} tone="bayside" />
                        <StatCard label="Soci con disclaimer" value={stats.disclaimer_accepted_users} tone="magenta" />
                        <StatCard label="Contatti totali" value={stats.total_inquiries} tone="emerald" />
                    </div>
                )}

                <div className="mt-8 flex items-center gap-2">
                    <FilterPill active={filter === "active"} onClick={() => setFilter("active")} testid="m-filter-active">Solo attive</FilterPill>
                    <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testid="m-filter-all">Tutte (incl. archivio)</FilterPill>
                </div>

                {loading ? (
                    <div className="bento p-12 text-center text-zinc-500 mt-6"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                ) : listings.length === 0 ? (
                    <div className="bento p-12 text-center text-zinc-500 mt-6" data-testid="admin-mercato-empty">
                        <Briefcase className="h-10 w-10 mx-auto mb-3 opacity-40" />
                        Nessuna inserzione da moderare.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="admin-listings-grid">
                        {listings.map(l => (
                            <div key={l.id} className={`bento p-5 ${l.is_active ? "" : "opacity-60"}`} data-testid={`admin-listing-${l.id}`}>
                                <div className="flex items-start justify-between mb-3">
                                    <span className="text-[10px] uppercase tracking-widest text-bayside font-grotesk">Quota {l.quota_category}</span>
                                    {l.is_active ? (
                                        <span className="text-[9px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-400/30 px-2 py-0.5 rounded-full uppercase tracking-widest font-grotesk">Attiva</span>
                                    ) : (
                                        <span className="text-[9px] font-bold text-zinc-400 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full uppercase tracking-widest font-grotesk">Archiviata</span>
                                    )}
                                </div>
                                <div className="font-display text-2xl">{l.quantity}%</div>
                                <div className="text-bayside font-display text-lg mt-1">€{l.valore_richiesto.toLocaleString("it-IT")}</div>
                                <div className="text-[10px] text-zinc-500 font-mono mt-3">{l.anonymous_label}</div>
                                {l.note && <p className="text-zinc-400 text-xs mt-3 line-clamp-3 whitespace-pre-line">{l.note}</p>}
                                {l.disclaimer_accepted_at && (
                                    <div className="mt-3 text-[10px] text-zinc-500 font-grotesk border-t border-zinc-800 pt-2" data-testid={`disclaimer-audit-${l.id}`}>
                                        <span className="text-bayside">✓ Disclaimer accettato</span> il {new Date(l.disclaimer_accepted_at).toLocaleString("it-IT")}
                                        {l.disclaimer_version && <span className="text-zinc-600 ml-1">(v. {l.disclaimer_version})</span>}
                                    </div>
                                )}
                                {l.deactivation_reason && (
                                    <p className="text-vermillion text-[11px] mt-3 font-grotesk italic">Motivo disattivazione: {l.deactivation_reason}</p>
                                )}
                                {l.is_active && (
                                    <button onClick={() => setTarget(l)} className="mt-4 text-vermillion text-xs uppercase tracking-widest font-grotesk px-3 py-2 border border-vermillion/30 hover:bg-vermillion/10 rounded-full transition w-full" data-testid={`deactivate-${l.id}`}>
                                        Disattiva con motivazione
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                    </>
                ) : (
                    <DisclaimerEditor />
                )}

                {target && (
                    <DeactivateModal listing={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); }} />
                )}
            </div>
        </AppLayout>
    );
}

function TabBtn({ active, onClick, children, testid, icon: Icon }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-grotesk uppercase tracking-widest transition ${
                active ? "text-white border-b-2 border-bayside" : "text-zinc-500 hover:text-white"
            }`}
        >
            <Icon className="h-4 w-4" /> {children}
        </button>
    );
}

function DisclaimerEditor() {
    const [current, setCurrent] = useState(null);
    const [history, setHistory] = useState([]);
    const [version, setVersion] = useState("");
    const [text, setText] = useState("");
    const [suggested, setSuggested] = useState("");
    const [saving, setSaving] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const load = async () => {
        const [c, h, s] = await Promise.all([
            apiPrivate.get("/admin/market/disclaimer-config"),
            apiPrivate.get("/admin/market/disclaimer-history"),
            apiPrivate.get("/admin/market/disclaimer-suggest-version"),
        ]);
        setCurrent(c.data);
        setHistory(h.data || []);
        setSuggested(s.data.suggested);
        setVersion(c.data.version);
        setText(c.data.text);
    };
    useEffect(() => { load(); }, []);

    const isVersionChanged = current && version !== current.version;
    const isTextChanged = current && text !== current.text;
    const hasChanges = isVersionChanged || isTextChanged;

    const publish = async () => {
        setSaving(true);
        try {
            await apiPrivate.put("/admin/market/disclaimer-config", { version, text });
            toast.success(
                isVersionChanged
                    ? `Versione ${version} pubblicata. I soci dovranno ri-accettare.`
                    : "Testo aggiornato (stessa versione, nessuna ri-accettazione)."
            );
            setShowConfirm(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
        setSaving(false);
    };

    if (!current) {
        return <div className="bento p-12 text-center text-zinc-500 mt-8"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
    }

    return (
        <div className="mt-8" data-testid="disclaimer-editor">
            {/* Stato corrente */}
            <div className="bento p-5 flex flex-wrap items-center justify-between gap-4 mb-6" data-testid="disclaimer-current-status">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">In produzione</div>
                    <div className="font-display text-2xl mt-1 flex items-center gap-3">
                        <span className="font-mono">{current.version}</span>
                        <span className="text-xs text-zinc-500 font-grotesk normal-case tracking-normal">
                            {current.acceptances_count} accettazioni
                        </span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">Pubblicato</div>
                    <div className="text-sm font-grotesk mt-1">
                        {current.published_at
                            ? `${new Date(current.published_at).toLocaleString("it-IT")} · ${current.published_by || "system"}`
                            : <span className="text-vermillion">Mai pubblicato (testo fallback)</span>}
                    </div>
                </div>
            </div>

            {/* PDF firmato */}
            <PdfManager current={current} onChange={load} />

            {/* Editor */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bento p-6">
                    <div className="flex items-center justify-between mb-4">
                        <span className="label-tag">Editor</span>
                        {isVersionChanged && (
                            <span className="text-[10px] uppercase tracking-widest font-grotesk px-2 py-1 rounded-full bg-magenta/15 text-magenta border border-magenta/30">
                                Forza ri-accettazione
                            </span>
                        )}
                    </div>
                    <div>
                        <div className="label-tag mb-2">Versione</div>
                        <div className="flex gap-2">
                            <input
                                value={version}
                                onChange={(e) => setVersion(e.target.value)}
                                data-testid="disclaimer-version-input"
                                placeholder="es. v2-2026-06"
                                className="flex-1 bg-[#0F0F0F] border border-white/10 text-white px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-bayside rounded-xl"
                            />
                            <button
                                type="button"
                                onClick={() => setVersion(suggested)}
                                title="Suggerisci versione successiva"
                                data-testid="disclaimer-suggest-version"
                                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-grotesk text-bayside border border-bayside/40 hover:bg-bayside/10 px-3 py-2 rounded-full transition"
                            >
                                <Wand2 className="h-3.5 w-3.5" /> {suggested}
                            </button>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-2 font-grotesk leading-relaxed">
                            Se cambi versione, tutti i soci che avevano accettato vedranno di nuovo il disclaimer al prossimo accesso. Se modifichi solo il testo mantenendo la stessa versione, le accettazioni esistenti restano valide (uso refusi/correzioni minori).
                        </p>
                    </div>
                    <div className="mt-5">
                        <div className="label-tag mb-2">Testo (Markdown supportato)</div>
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={18}
                            data-testid="disclaimer-text-input"
                            placeholder="**Disclaimer Bacheca annunci soci**\n\n1. Punto uno..."
                            className="w-full bg-[#0F0F0F] border border-white/10 text-white p-3 font-mono text-sm focus:outline-none focus:border-bayside resize-y rounded-xl leading-relaxed"
                        />
                    </div>
                    <div className="flex items-center justify-between mt-5 flex-wrap gap-3">
                        <div className="flex items-center gap-2 text-xs font-grotesk">
                            {hasChanges ? (
                                <span className="text-magenta uppercase tracking-widest" data-testid="disclaimer-unsaved">● Modifiche non salvate</span>
                            ) : (
                                <span className="text-zinc-500 uppercase tracking-widest">Nessuna modifica</span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => { setVersion(current.version); setText(current.text); }}
                                disabled={!hasChanges}
                                data-testid="disclaimer-reset"
                                className="btn-ghost text-xs disabled:opacity-40"
                            >
                                Annulla modifiche
                            </button>
                            <button
                                onClick={() => setShowConfirm(true)}
                                disabled={!hasChanges || text.length < 20}
                                data-testid="disclaimer-publish"
                                className="btn-rebel"
                            >
                                <Save className="h-4 w-4" /> Pubblica
                            </button>
                        </div>
                    </div>
                </div>

                {/* Live preview */}
                <div className="bento p-6" data-testid="disclaimer-preview-wrapper">
                    <div className="flex items-center gap-2 mb-4">
                        <Eye className="h-4 w-4 text-bayside" />
                        <span className="label-tag">Anteprima live (come la vedrà il socio)</span>
                    </div>
                    <div className="bg-[#01172d] border border-white/10 rounded-2xl p-6">
                        <span className="label-tag text-magenta">— Bacheca annunci soci</span>
                        <h2 className="font-display text-3xl mt-2 tracking-tighter">
                            Prima di entrare, <span className="font-serif-italic text-gradient-tropical">una nota</span>.
                        </h2>
                        <div
                            className="mt-6 text-sm text-zinc-300 whitespace-pre-line leading-relaxed"
                            data-testid="disclaimer-preview"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(text), { ALLOWED_TAGS: ["br", "b", "i", "em", "strong", "u", "a", "p", "code", "ul", "ol", "li", "h1", "h2", "h3"], ALLOWED_ATTR: ["href", "target", "rel"] }) }}
                        />
                        <div className="mt-6 flex items-start gap-3 text-sm text-zinc-300 border-t border-white/10 pt-4">
                            <input type="checkbox" disabled className="mt-1 accent-bayside" />
                            <span>Ho letto e accetto le condizioni d'uso (versione <code className="text-bayside font-mono">{version || "—"}</code>).</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* History */}
            <div className="mt-8">
                <button
                    onClick={() => setShowHistory(s => !s)}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-grotesk text-zinc-400 hover:text-white"
                    data-testid="disclaimer-toggle-history"
                >
                    <History className="h-4 w-4" />
                    Storico versioni {history.length > 0 ? `(${history.length})` : ""}
                </button>
                {showHistory && (
                    <div className="mt-4 space-y-3" data-testid="disclaimer-history-list">
                        {history.length === 0 ? (
                            <div className="bento p-6 text-zinc-500 text-sm text-center">Nessuna versione archiviata.</div>
                        ) : (
                            history.map(h => (
                                <details key={h.version} className="bento p-5 group" data-testid={`disclaimer-history-${h.version}`}>
                                    <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <span className="font-mono text-bayside">{h.version}</span>
                                            <span className="text-xs text-zinc-500 font-grotesk">
                                                {h.published_at ? new Date(h.published_at).toLocaleString("it-IT") : "—"}
                                                {h.published_by && ` · ${h.published_by}`}
                                            </span>
                                        </div>
                                        <span className="text-[10px] uppercase tracking-widest font-grotesk text-zinc-500">{h.acceptances_count} accettazioni</span>
                                    </summary>
                                    <pre className="mt-3 text-xs text-zinc-300 whitespace-pre-wrap font-mono bg-black/30 p-3 rounded-xl border border-white/5">{h.text}</pre>
                                    <button
                                        onClick={() => { setVersion(h.version + "-restored"); setText(h.text); toast.info(`Versione ${h.version} caricata nell'editor. Modifica la versione e pubblica per ripristinare.`); }}
                                        className="btn-ghost mt-3 text-xs"
                                        data-testid={`disclaimer-restore-${h.version}`}
                                    >
                                        Carica nell'editor
                                    </button>
                                </details>
                            ))
                        )}
                    </div>
                )}
            </div>

            {showConfirm && (
                <PublishConfirmModal
                    isVersionChanged={isVersionChanged}
                    currentAcceptCount={current.acceptances_count}
                    newVersion={version}
                    saving={saving}
                    onCancel={() => setShowConfirm(false)}
                    onConfirm={publish}
                />
            )}
        </div>
    );
}

function PublishConfirmModal({ isVersionChanged, currentAcceptCount, newVersion, saving, onCancel, onConfirm }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={!saving ? onCancel : undefined} data-testid="disclaimer-confirm-modal">
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className={`h-6 w-6 shrink-0 mt-1 ${isVersionChanged ? "text-magenta" : "text-bayside"}`} />
                    <div>
                        <h3 className="font-display text-2xl tracking-tight">Conferma pubblicazione</h3>
                        {isVersionChanged ? (
                            <p className="text-zinc-400 text-sm mt-2">
                                Stai pubblicando una <strong className="text-white">nuova versione "{newVersion}"</strong>. Le <strong className="text-magenta">{currentAcceptCount} accettazioni</strong> precedenti rimarranno archiviate, ma tutti i soci vedranno di nuovo il gate al prossimo accesso alla Bacheca annunci soci.
                            </p>
                        ) : (
                            <p className="text-zinc-400 text-sm mt-2">
                                Stai aggiornando il testo della versione corrente <strong className="text-white">"{newVersion}"</strong>. Le accettazioni esistenti rimarranno valide (nessuna ri-accettazione richiesta).
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={onCancel} disabled={saving} className="btn-ghost" data-testid="publish-cancel">Annulla</button>
                    <button onClick={onConfirm} disabled={saving} className="btn-rebel" data-testid="publish-confirm">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pubblica"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Minimal markdown renderer (bold, italic, lists, paragraphs) — no XSS risk because
// admin-only input and we strip all HTML tags from source before rendering.
function renderMarkdown(src) {
    if (!src) return "<p class='text-zinc-600 italic'>Inizia a scrivere…</p>";
    // Strip any embedded HTML to keep it safe
    let safe = src.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
    // Bold and italic
    safe = safe.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    safe = safe.replace(/(^|[^*])\*(?!\s)(.+?)\*/g, "$1<em>$2</em>");
    // Headings ## and ###
    safe = safe.replace(/^### (.+)$/gm, "<h3 class='font-display text-xl mt-4 mb-2'>$1</h3>");
    safe = safe.replace(/^## (.+)$/gm, "<h2 class='font-display text-2xl mt-4 mb-3'>$1</h2>");
    // Ordered/unordered lists
    safe = safe.replace(/^(\d+)\.\s+(.+)$/gm, "<li class='ml-5 list-decimal'>$2</li>");
    safe = safe.replace(/^[-*]\s+(.+)$/gm, "<li class='ml-5 list-disc'>$1</li>");
    // Paragraphs (double-newline split)
    return safe.split(/\n\n+/).map(p => {
        if (p.includes("<li")) return `<ul class='space-y-1 my-2'>${p}</ul>`;
        if (p.match(/^<h\d/)) return p;
        return `<p class='my-2 leading-relaxed'>${p.replace(/\n/g, "<br/>")}</p>`;
    }).join("\n");
}

function StatCard({ label, value, tone }) {
    const cl = tone === "bayside" ? "text-bayside" : tone === "magenta" ? "text-magenta" : tone === "emerald" ? "text-emerald-300" : "text-white";
    return (
        <div className="bento p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">{label}</div>
            <div className={`font-display text-3xl mt-1 ${cl}`}>{value}</div>
        </div>
    );
}

function FilterPill({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`text-xs uppercase tracking-widest font-grotesk px-3 py-2 rounded-full border transition ${active ? "border-bayside bg-bayside/15 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}
        >{children}</button>
    );
}

function DeactivateModal({ listing, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        setSaving(true);
        try {
            await apiPrivate.post(`/admin/market/listings/${listing.id}/deactivate`, { reason });
            toast.success("Inserzione disattivata");
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
            setSaving(false);
        }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="deactivate-modal">
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-3">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-vermillion mt-1" />
                        <div>
                            <span className="label-tag">— Disattivazione</span>
                            <h2 className="font-display text-2xl tracking-tight mt-2">Disattiva inserzione</h2>
                        </div>
                    </div>
                    <button onClick={onClose}><X className="h-5 w-5 text-zinc-500" /></button>
                </div>
                <p className="text-zinc-400 text-sm mt-3">La motivazione sarà archiviata nel log. L'inserzionista vedrà l'inserzione come "archiviata".</p>
                <textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Es. Violazione condizioni d'uso, prezzo non realistico, ecc." data-testid="deactivate-reason" className="w-full mt-4 bg-[#0F0F0F] border border-white/10 text-white p-3 font-grotesk text-sm focus:outline-none focus:border-vermillion resize-none rounded-xl" />
                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={onClose} className="btn-ghost" data-testid="deactivate-cancel">Annulla</button>
                    <button onClick={submit} disabled={saving || reason.length < 3} className="btn-rebel" data-testid="deactivate-confirm">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disattiva"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function PreemptionPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("pending");

    const load = async () => {
        setLoading(true);
        try {
            const q = filter === "all" ? "" : `?status=${filter}`;
            const r = await apiPrivate.get(`/admin/market/preemption${q}`);
            setItems(r.data || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const act = async (req, action) => {
        const note = window.prompt(action === "approve" ? "Nota interna (opzionale) — verrà mostrata al socio:" : "Motivo del rifiuto (verrà mostrato al socio):");
        if (action === "reject" && !note) return;
        try {
            await apiPrivate.post(`/admin/market/preemption/${req.id}/${action}`, { admin_note: note || null });
            toast.success(action === "approve" ? "Prelazione verificata — il socio può pubblicare" : "Richiesta respinta");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
    };

    return (
        <div className="mt-8" data-testid="preemption-panel">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <h2 className="text-2xl font-display tracking-tighter">Richieste di prelazione Quota A</h2>
                    <p className="text-sm text-zinc-500 mt-1">Il socio invia comunicazione a <code className="text-bayside">investorelations@startupitalia.eu</code>; dopo verifica statutaria, sblocca qui la pubblicazione dell&apos;annuncio.</p>
                </div>
                <div className="flex gap-1">
                    {[["pending", "In attesa"], ["approved", "Approvate"], ["rejected", "Respinte"], ["all", "Tutte"]].map(([k, l]) => (
                        <button key={k} onClick={() => setFilter(k)} className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border transition ${filter === k ? "border-bayside text-bayside" : "border-zinc-800 text-zinc-500 hover:text-white"}`} data-testid={`filter-${k}`}>{l}</button>
                    ))}
                </div>
            </div>
            {loading ? (
                <div className="p-8 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
            ) : items.length === 0 ? (
                <div className="bento p-12 text-center text-zinc-500" data-testid="preemption-empty">Nessuna richiesta in questo stato.</div>
            ) : (
                <div className="space-y-3">
                    {items.map(r => (
                        <div key={r.id} className="bento p-5 flex items-center justify-between gap-3 flex-wrap" data-testid={`preemption-row-${r.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`px-2 py-0.5 rounded-full text-xs ${r.status === "pending" ? "bg-magenta/15 text-magenta" : r.status === "approved" ? "bg-bayside/15 text-bayside" : "bg-vermillion/15 text-vermillion"}`}>
                                        {r.status === "pending" ? "In attesa" : r.status === "approved" ? "Verificata" : "Respinta"}
                                    </span>
                                    <span className="px-2 py-0.5 rounded-full text-xs bg-bayside/15 text-bayside">Quota {r.quota_category}</span>
                                    {r.consumed && <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-700/40 text-zinc-400">Consumata</span>}
                                </div>
                                <div className="font-semibold mt-2">{r.user_display}</div>
                                <div className="text-xs text-zinc-500">{r.user_email}</div>
                                <div className="text-xs text-zinc-500 mt-1">Richiesta: {new Date(r.created_at).toLocaleString("it-IT")}</div>
                                {r.admin_note && <div className="text-xs text-zinc-400 mt-2 italic">Nota: {r.admin_note}</div>}
                            </div>
                            {r.status === "pending" && (
                                <div className="flex gap-2 shrink-0">
                                    <button onClick={() => act(r, "reject")} className="btn-ghost text-xs text-vermillion" data-testid={`reject-${r.id}`}>Respingi</button>
                                    <button onClick={() => act(r, "approve")} className="btn-rebel" data-testid={`approve-${r.id}`}>
                                        <ShieldCheck className="h-3.5 w-3.5" /> Prelazione verificata
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


function PdfManager({ current, onChange }) {
    const [uploading, setUploading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const fileInputId = "disclaimer-pdf-file-input";

    const upload = async (file) => {
        if (!file) return;
        if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
            toast.error("Seleziona un file PDF"); return;
        }
        if (file.size > 20 * 1024 * 1024) { toast.error("Il file supera i 20 MB"); return; }
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            await apiPrivate.post("/admin/market/disclaimer-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("PDF caricato — ora è disponibile ai soci");
            onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore upload");
        } finally { setUploading(false); }
    };

    const download = async () => {
        try {
            const res = await apiPrivate.get("/market/disclaimer-pdf", { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = current.pdf_filename || "disclaimer-mercato-secondario.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 5000);
        } catch (e) {
            toast.error("Errore download");
        }
    };

    const remove = async () => {
        setUploading(true);
        try {
            await apiPrivate.delete("/admin/market/disclaimer-pdf");
            toast.success("PDF rimosso");
            setConfirmDelete(false);
            onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally { setUploading(false); }
    };

    return (
        <div className="bento p-5 mb-6" data-testid="disclaimer-pdf-manager">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                    <span className="label-tag">— PDF firmato</span>
                    <h3 className="font-display text-xl tracking-tight mt-2">
                        Documento ufficiale <span className="font-serif-italic text-gradient-tropical">di responsabilità</span>.
                    </h3>
                    <p className="text-xs text-zinc-500 mt-2 font-grotesk leading-relaxed max-w-xl">
                        Carica il PDF firmato del disclaimer. Sarà scaricabile dai soci dalla pagina della Bacheca annunci soci e nel gate di prima accettazione. Sostituisce automaticamente qualsiasi versione precedente.
                    </p>
                </div>
                <div className="flex flex-col items-end gap-2 text-right">
                    {current.pdf_available ? (
                        <>
                            <div className="flex items-center gap-2 text-magenta text-xs uppercase tracking-widest font-grotesk">
                                <FileText className="h-3.5 w-3.5" /> Caricato
                            </div>
                            <div className="text-sm text-white font-mono truncate max-w-[240px]" data-testid="disclaimer-pdf-filename">{current.pdf_filename}</div>
                            {current.pdf_uploaded_at && (
                                <div className="text-[10px] text-zinc-500 font-grotesk">
                                    {new Date(current.pdf_uploaded_at).toLocaleString("it-IT")}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-xs uppercase tracking-widest font-grotesk text-vermillion flex items-center gap-2">
                            <AlertTriangle className="h-3.5 w-3.5" /> Nessun PDF
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
                <label htmlFor={fileInputId} className="btn-rebel cursor-pointer" data-testid="disclaimer-pdf-upload-label">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {current.pdf_available ? "Sostituisci PDF" : "Carica PDF"}
                </label>
                <input
                    id={fileInputId}
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => upload(e.target.files?.[0])}
                    disabled={uploading}
                    className="hidden"
                    data-testid="disclaimer-pdf-file-input"
                />
                {current.pdf_available && (
                    <>
                        <button onClick={download} className="btn-ghost text-xs" data-testid="disclaimer-pdf-download">
                            <Download className="h-3.5 w-3.5" /> Scarica
                        </button>
                        <button onClick={() => setConfirmDelete(true)} disabled={uploading} className="btn-ghost text-xs text-vermillion" data-testid="disclaimer-pdf-delete">
                            <Trash2 className="h-3.5 w-3.5" /> Rimuovi
                        </button>
                    </>
                )}
            </div>

            {confirmDelete && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => !uploading && setConfirmDelete(false)}>
                    <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <h4 className="font-display text-xl">Rimuovere il PDF?</h4>
                        <p className="text-sm text-zinc-400 mt-2">I soci non potranno più scaricare il PDF firmato finché non ne caricherai uno nuovo. Il testo del disclaimer resterà invariato.</p>
                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={() => setConfirmDelete(false)} className="btn-ghost text-sm">Annulla</button>
                            <button onClick={remove} disabled={uploading} className="btn-rebel" data-testid="disclaimer-pdf-confirm-delete">
                                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Rimuovi
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
